import React, { CSSProperties, FC, useRef } from 'react'

import { Carousel } from '@mantine/carousel'
import { clsx } from '@mantine/core'
import Autoplay from 'embla-carousel-autoplay'

import HomepageBanner from 'components/molecules/homepageBanner'
import { HomepageBannerType } from 'interfaces/types/Banner'

import {
  StyledHomeBanner,
  StyledHomeBannerCarousel,
  StyledHomeBannerSide,
  StyledHomeBannerSideWrapper,
} from './HomeBanner.styled'
import { HomeBannerImage } from './HomeBannerImage'

export type HomeBannerProps = {
  isMobile?: boolean
  homeBanner?: HomepageBannerType
  style?: CSSProperties
  className?: string
  container?: React.ElementType
  containerProps?: Record<string, any>
}

export const HomeBanner: FC<HomeBannerProps> = (props) => {
  const {
    isMobile,
    homeBanner,
    style,
    className,
    container: Container = 'div',
    containerProps,
  } = props
  const { autoSlideTiming = 0, listBanners: banners = [] } =
    homeBanner?.banners || {}

  const autoplay = useRef(
    Autoplay({
      delay: Number(autoSlideTiming) * 1000,
      stopOnInteraction: false,
    }),
  )

  const sideBanner = { ...homeBanner?.side_banner }
  const sideBannerProps = homeBanner?.side_banner?.url
    ? {
        'data-event-category': 'Side Banner',
        'data-event-action': 'Banner Click',
        'data-event-label': homeBanner.side_banner.url,
      }
    : undefined
  const showSideBanner =
    !!homeBanner?.side_banner?.desktop && !!homeBanner?.side_banner?.mobile

  if (!homeBanner) {
    return null
  }

  if (homeBanner.type === 'banner') {
    return (
      <StyledHomeBannerSide
        className={clsx(className, 'homeSideBanner')}
        style={style}
      >
        <Container {...containerProps}>
          <StyledHomeBannerSideWrapper className="homeSideBannerWrapper">
            <div className="homeSideBannerMain">
              <StyledHomeBannerCarousel
                withControls={false}
                withIndicators={banners?.length > 1}
                slideGap={0}
                loop
                plugins={autoSlideTiming > 0 ? [autoplay.current] : []}
                styles={(theme) => ({
                  indicator: {
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    border: `1px solid ${theme.colors.neutral100}`,
                    boxShadow: 'none',
                    background: '#fff',
                    opacity: 1,
                    '&[data-active]': {
                      borderColor: theme.fn.primaryColor(),
                      background: theme.fn.primaryColor(),
                    },
                  },
                })}
              >
                {banners.map((i, idx) => (
                  <Carousel.Slide key={idx}>
                    <HomeBannerImage
                      isMobile={isMobile}
                      banner={i}
                      bannerProps={
                        i.url
                          ? {
                              'data-event-category': 'Slicing Banners',
                              'data-event-action': 'Banner Click',
                              'data-event-label': i.url,
                            }
                          : undefined
                      }
                      desktopRatio={774 / 168}
                      mobileRatio={343 / 154}
                      className="homeBannerMain"
                    />
                  </Carousel.Slide>
                ))}
              </StyledHomeBannerCarousel>
            </div>
            {showSideBanner && (
              <div
                className="homeSideBannerSide"
                // style={{ '--homeBannerSide-width': `${(360 / 1128) * 100}%` }}
              >
                <HomeBannerImage
                  isMobile={isMobile}
                  banner={sideBanner}
                  bannerProps={sideBannerProps}
                  desktopRatio={327 / 168}
                  mobileRatio={343 / 80}
                  className="homeBannerSide"
                />
              </div>
            )}
          </StyledHomeBannerSideWrapper>
        </Container>
      </StyledHomeBannerSide>
    )
  }

  return (
    <StyledHomeBanner className="homebanner">
      <HomepageBanner
        isMobile={!!isMobile}
        autoplayTime={homeBanner?.banners?.autoSlideTiming}
        banners={homeBanner?.banners?.listBanners || []}
        title={homeBanner?.banners?.title || ''}
        description={homeBanner?.banners?.description || ''}
        image={homeBanner?.banners?.image || ''}
        type={homeBanner?.type || ''}
        ctaText={homeBanner?.banners?.ctaText || ''}
        ctaUrl={homeBanner?.banners?.ctaUrl || ''}
      />
    </StyledHomeBanner>
  )
}
